import {
  MultiCurrencyPrice,
  Price,
  TravelDocumentPrice,
} from '@dapi/sdk';
import {
  createMultiCurrencyPriceFromPrice,
} from '@dapi/sdk/helpers';
import {
  createMultiCurrencyAirPricingRecords,
  createMultiCurrencyPrice,
  createMultiCurrencyUnitPrice,
  createPrice,
  MockMultiCurrencyAirPricingRecords,
  MockMultiCurrencyPrice,
  MockMultiCurrencyUnitPrice,
  MockPrice,
} from '@dapi/sdk/spec';
import {
  zeroAmountInSek,
} from './amount';
import {
  currency,
  currencyCode,
  sekCurrency,
} from './currency';
import {
  ticketingFee,
} from './fee';
import {
  arnEwrFlightItem,
  cphOslFlightItem,
} from './flights';
import {
  mockMilesConversion,
  mockMilesConversionWithoutCancellation,
} from './miles-conversion';
import {
  surcharge,
} from './surcharge';
import {
  arnEwrTaxes,
  multicurrencyTaxes,
  multipleTaxes,
  multipleTaxesForMiles,
  multipleTaxesForMilesWithTwoPax,
  taxes,
  taxesForMultiBoundRebooking,
  taxesForPaidAmount,
  taxesForPaidAmountWithMiles,
} from './tax';
import {
  traveler,
  traveler2,
} from './traveler';

export const multicurrencyPrice: MultiCurrencyPrice = createMultiCurrencyPrice({
  template: {
    base: {
      value: 139_900,
      currency,
      currencyCode
    },
    fees: [ticketingFee],
    taxes: multicurrencyTaxes,
    total: {
      value: 145_307,
      currency,
      currencyCode
    },
    totalFees: {
      value: 1492,
      currency,
      currencyCode
    },
    totalTaxes: {
      value: 3915,
      currency,
      currencyCode
    }
  }
});

export const zeroPriceWithTaxes: MockPrice = createPrice({
  template: {
    total: 0,
    taxes: taxes,
    totalTaxes: taxes[0].value
  }
});

export const priceWithTaxes: MockPrice = createPrice({
  template: {
    base: 1830,
    currencyCode: 'AED',
    surcharges: [surcharge],
    taxes: multipleTaxes,
    total: 2795,
    totalSurcharges: 670,
    totalTaxes: 295
  }
});

export const priceAirBound: MockPrice = createPrice({
  template: {
    base: 150_000,
    total: 200_000,
    totalTaxes: 50_000
  }
});

export const unitPriceMulticurrencyForUnOnly: MockPrice = createPrice({
  template: {
    base: 28_000,
    total: 31_720,
    taxes: taxes,
    totalTaxes: taxes[0].value,
    fees: [ticketingFee],
    totalFees: ticketingFee.value
  }
});

export const totalPriceMulticurrencyForUnOnly: MockPrice = createPrice({
  template: {
    base: 28_000,
    total: 31_720,
    totalTaxes: taxes[0].value,
    totalFees: ticketingFee.value
  }
});

export const unitMultiCurrencyPrice: MultiCurrencyPrice = {
  ...createMultiCurrencyPriceFromPrice(unitPriceMulticurrencyForUnOnly)
};

export const totalMultiCurrencyPrice: MultiCurrencyPrice = {
  ...createMultiCurrencyPriceFromPrice(totalPriceMulticurrencyForUnOnly)
};

export const multiCurrencyAirPricingRecords: MockMultiCurrencyAirPricingRecords = createMultiCurrencyAirPricingRecords({
  template: {
    unitPrices: [
      createMultiCurrencyUnitPrice({
        template: {
          travelerIds: [traveler.id],
          flightIds: [cphOslFlightItem.id],
          prices: [unitMultiCurrencyPrice]
        }
      })
    ],
    totalPrices: [totalMultiCurrencyPrice]
  }
});

export const unitPriceMulticurrency: MockMultiCurrencyUnitPrice = createMultiCurrencyUnitPrice({
  template: {
    travelerIds: [traveler.id],
    flightIds: [cphOslFlightItem.id, cphOslFlightItem.id],
    prices: [multicurrencyPrice]
  }
});

export const totalPriceMulticurrency: MockMultiCurrencyPrice = createMultiCurrencyPrice({
  template: {
    base: {
      value: 139_900,
      currency,
      currencyCode
    },
    total: {
      value: 145_307,
      currency,
      currencyCode
    },
    totalFees: {
      value: 1492,
      currency,
      currencyCode
    },
    totalTaxes: {
      value: 3915,
      currency,
      currencyCode
    }
  }
});

export const servicePrice: MockMultiCurrencyPrice = createMultiCurrencyPrice({
  template: {
    base: {
      value: 6500,
      currency,
      currencyCode: 'SEK'
    },
    total: {
      value: 6500,
      currency,
      currencyCode: 'SEK'
    }
  }
});

export const multiCurrencyAirPricingRecordsForTwoBoundFlight = createMultiCurrencyAirPricingRecords({
  template: {
    unitPrices: [unitPriceMulticurrency],
    totalPrices: [totalPriceMulticurrency]
  }
});

export const servicesPricing = createMultiCurrencyAirPricingRecords({
  template: {
    unitPrices: [unitPriceMulticurrency],
    totalPrices: [totalPriceMulticurrency]
  }
});

export const multiBoundRebookingPrice: Price = {
  base: 48_400,
  total: 65_948,
  currencyCode,
  currency,
  taxes: taxesForMultiBoundRebooking,
  totalTaxes: 17_548
};

export const outBoundDisruptedObFeePrice: TravelDocumentPrice = {
  base: 23_200,
  total: 38_215,
  currencyCode: 'EUR',
  currency,
  taxes: [taxesForPaidAmount[0], taxesForPaidAmount[2], taxesForPaidAmount[3], taxesForPaidAmount[4]],
  totalTaxes: 11_215,
  surcharges: [{
    ...surcharge,
    value: 400,
    currencyCode: 'EUR',
    currency,
    code: 'YQ'
  }, {
    ...surcharge,
    value: 3400,
    currencyCode: 'EUR',
    currency,
    code: 'YQ'
  }],
  totalSurcharges: 3800,
  priceBreakdowns: [{
    currencyCode: 'NUC',
    amount: '122.23',
    value: 12_223,
    priceQualifier: 'BASE_FARE',
    priceBreakdownType: 'PER_FARE_FAMILY',
    applicability: [
      {
        id: 'fareInfo-1',
        type: 'fareInfo'
      }
    ]
  },
  {
    currencyCode: 'NUC',
    amount: '122.23',
    value: 12_223,
    priceQualifier: 'BASE_FARE',
    priceBreakdownType: 'PER_FARE_FAMILY',
    applicability: [
      {
        id: 'fareInfo-2',
        type: 'fareInfo'
      }
    ]
  }]
};

const unitPriceForTwoPax: MockMultiCurrencyPrice = createMultiCurrencyPrice({
  template: {
    base: {
      value: 12_593,
      currencyCode: 'SEK'
    },
    total: {
      value: 14_361,
      currencyCode: 'SEK'
    },
    taxes: arnEwrTaxes,
    totalTaxes: {
      value: 1768,
      currencyCode: 'SEK',
      currency: sekCurrency
    }
  }
});

const totalPriceForTwoPax: MockMultiCurrencyPrice = createMultiCurrencyPrice({
  template: {
    base: {
      value: 34_631,
      currencyCode: 'SEK'
    },
    total: {
      value: 39_935,
      currencyCode: 'SEK'
    },
    totalTaxes: {
      value: 5304,
      currencyCode: 'SEK'
    }
  }
});

export const arnEwrAirPrices: MockMultiCurrencyAirPricingRecords = createMultiCurrencyAirPricingRecords({
  template: {
    unitPrices: [
      createMultiCurrencyUnitPrice({
        template: {
          travelerIds: [traveler.id, traveler2.id],
          flightIds: [arnEwrFlightItem.id],
          prices: [unitPriceForTwoPax]
        }
      })
    ],
    totalPrices: [totalPriceForTwoPax]
  }
});

const unitPriceForMilesAirPrice: MultiCurrencyPrice[] = [{
  base: zeroAmountInSek,
  total: {
    ...zeroAmountInSek,
    value: 5686
  },
  taxes: taxesForPaidAmountWithMiles,
  totalTaxes: {
    ...zeroAmountInSek,
    value: 5686
  }
}];

const unitPriceForMilesAirPriceWithoutCancellation: MultiCurrencyPrice[] = [{
  base: zeroAmountInSek,
  total: {
    ...zeroAmountInSek,
    value: 5825
  },
  taxes: multipleTaxesForMiles,
  totalTaxes: {
    ...zeroAmountInSek,
    value: 5825
  }
}];

export const mockMilesAirPrice: MockMultiCurrencyAirPricingRecords = createMultiCurrencyAirPricingRecords({
  template: {
    unitPrices: [createMultiCurrencyUnitPrice({
      template: {
        flightIds: [arnEwrFlightItem.id],
        prices: unitPriceForMilesAirPrice,
        milesConversion: mockMilesConversion
      }
    })],
    milesConversion: mockMilesConversion,
    totalPrices: [{
      ...unitPriceForMilesAirPrice[0],
      taxes: undefined
    }]
  }
});

export const mockMilesAirPriceForTwoPassengers: MockMultiCurrencyAirPricingRecords = {
  ...mockMilesAirPrice,
  totalPrices: [{
    base: zeroAmountInSek,
    total: {
      ...zeroAmountInSek,
      value: 11_372
    },
    taxes: multipleTaxesForMilesWithTwoPax,
    totalTaxes: {
      ...zeroAmountInSek,
      value: 11_372
    }
  }]
};

export const mockMilesAirPriceWithoutCancellation: MockMultiCurrencyAirPricingRecords = createMultiCurrencyAirPricingRecords({
  template: {
    unitPrices: [createMultiCurrencyUnitPrice({
      template: {
        travelerIds: [traveler.id],
        flightIds: [arnEwrFlightItem.id],
        prices: unitPriceForMilesAirPriceWithoutCancellation,
        milesConversion: mockMilesConversionWithoutCancellation
      }
    })],
    milesConversion: mockMilesConversionWithoutCancellation,
    totalPrices: [{
      ...unitPriceForMilesAirPriceWithoutCancellation[0],
      taxes: undefined
    }]
  }
});
